import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MemberWall } from "@/components/MemberWall";
import { ChallengeCard } from "@/components/ChallengeCard";
import { BuddiesPanel } from "@/components/BuddiesPanel";
import { BuddyCheers } from "@/components/BuddyCheers";
import { HandlePicker } from "@/components/HandlePicker";
import { SharingSettings } from "@/components/community/SharingSettings";
import { CategoryNav, CategoryHero, MemberList, NotesInbox } from "@/components/community/CommunityParts";
import { WinsFeed } from "@/components/community/WinsFeed";
import { RainDayBanner } from "@/components/community/RainDayBanner";

const EMPTY_ME = { share_progress: false, joined_categories: [], accept_suggestions: false, share_habits: false, community_handle: null };

export default function Community() {
  const [data, setData] = useState({ categories: [], me: EMPTY_ME, guidelines: [] });
  const [activeCat, setActiveCat] = useState(null);
  const [members, setMembers] = useState([]);
  const [myStreak, setMyStreak] = useState(0);
  const [wallMember, setWallMember] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [picking, setPicking] = useState(null);

  const load = async () => {
    const [r, ib, ch] = await Promise.all([api.get("/community/categories"), api.get("/community-inbox"), api.get("/community/challenges")]);
    setData(r.data);
    setInbox(ib.data.posts);
    setChallenges(ch.data.challenges);
    if (ch.data.challenges.some((c) => c.completed_now)) toast.success(`Challenge complete! Badge + ${ch.data.challenges.find((c) => c.completed_now).points} pts earned.`, { duration: 6000 });
    if (ch.data.challenges.some((c) => c.rained_now)) toast(`Rain Day! Your finish tipped the ${ch.data.challenges.find((c) => c.rained_now).category} garden over the line — everyone who joined got a shower.`, { duration: 8000 });
    const joined = r.data.me?.joined_categories || [];
    if (r.data.categories.length > 0) setActiveCat((cur) => cur || (r.data.categories.find((c) => joined.includes(c.category)) || r.data.categories[0]).category);
  };

  const loadMembers = async (cat) => {
    const r = await api.get(`/community/${cat}`);
    setMembers(r.data.members);
    setMyStreak(r.data.my_streak || 0);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (activeCat) { loadMembers(activeCat); setWallMember(null); } }, [activeCat]);

  const savePrefs = async (next) => {
    const r = await api.patch("/users/preferences", next);
    const { share_progress, joined_categories, accept_suggestions, share_habits, community_handle } = r.data;
    setData((d) => ({ ...d, me: { share_progress, joined_categories, accept_suggestions, share_habits, community_handle } }));
    toast.success(next.share_progress ? "Preferences saved." : "You're private. Your habits stay yours alone.");
    if (activeCat) loadMembers(activeCat);
    load();
  };

  const me = data.me;
  const toggleShare = () => {
    if (!me.share_progress && !me.community_handle) return setPicking("enable");
    savePrefs({ ...me, share_progress: !me.share_progress });
  };
  const saveHandle = async (handle) => {
    await savePrefs({ ...me, share_progress: picking === "enable" ? true : me.share_progress, community_handle: handle });
    setPicking(null);
  };
  const toggleCategory = (cat) => {
    const next = me.joined_categories.includes(cat) ? me.joined_categories.filter((c) => c !== cat) : [...me.joined_categories, cat];
    savePrefs({ ...me, joined_categories: next });
  };

  const inActiveCat = activeCat && me.joined_categories.includes(activeCat);
  const [needSharing, setNeedSharing] = useState(false);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 space-y-6 relative z-10">
      <div>
        <div className="text-xs font-bold uppercase tracking-widest text-amber-800">Community</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-stone-900 mt-1">You're not alone in this.</h1>
        <p className="text-stone-600 mt-2 max-w-2xl">
          Private by default. If you opt in, others in your categories see only your handle and level — and only if you choose, the names of your habits. Never your real name, notes, moods or check-ins.
        </p>
      </div>

      {picking && <HandlePicker current={picking === "change" ? me.community_handle : null} onSave={saveHandle} onClose={() => setPicking(null)} />}

      <SharingSettings me={me} categories={data.categories}
        onToggleShare={toggleShare} onToggleCategory={toggleCategory}
        onToggleSuggestions={() => savePrefs({ ...me, accept_suggestions: !me.accept_suggestions })}
        onToggleShareHabits={() => savePrefs({ ...me, share_habits: !me.share_habits })}
        onChangeHandle={() => setPicking("change")} />

      {me.share_progress && (
        <>
          <BuddyCheers />
          <WinsFeed />
          <BuddiesPanel eligibleHint={`${me.share_habits}-${me.joined_categories.join(",")}`} />
          <NotesInbox inbox={inbox} guidelines={data.guidelines} />
        </>
      )}

      <div className="grid lg:grid-cols-12 gap-6">
        <CategoryNav categories={data.categories} activeCat={activeCat} onSelect={setActiveCat} />
        <div className="lg:col-span-8">
          {activeCat && <CategoryHero category={activeCat} />}
          {activeCat && <div className="mb-4"><RainDayBanner key={activeCat} category={activeCat} /></div>}
          {inActiveCat && <ChallengeCard challenge={challenges.find((c) => c.category === activeCat)} onChange={() => { load(); loadMembers(activeCat); }} />}
          {needSharing && (
            <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-stone-800" data-testid="sharing-explainer">
              Turn on sharing to water gardens and cheer — others see only your handle and level, never your habit names unless you choose.
              <button onClick={() => { setNeedSharing(false); setPicking("enable"); }} data-testid="sharing-explainer-enable" className="ml-2 amber-btn text-xs py-1">Turn on sharing</button>
            </div>
          )}
          <MemberList members={members} canCheer={me.share_progress && inActiveCat} streak={myStreak} onWatered={(r) => { setMyStreak(r.streak ?? myStreak); }}
            gate={{ sharingOff: !me.share_progress, joinCat: me.share_progress && !inActiveCat ? activeCat : null, catLabel: activeCat ? activeCat[0].toUpperCase() + activeCat.slice(1) : "", onNeedSharing: () => setNeedSharing(true), onJoined: () => { load(); loadMembers(activeCat); } }}
            onOpenWall={(m) => setWallMember(wallMember?.id === m.id ? null : m)} />
          {wallMember && activeCat && (
            <div className="mt-4">
              <MemberWall category={activeCat} member={wallMember} onClose={() => setWallMember(null)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
